import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Container, GlassCard, CardMotion } from "@/components/site/Primitives";
import { SectionRail } from "@/components/site/SectionRail";
import { loadServiceCategories } from "@/lib/content-loader";
import type { ServiceCategory, ServiceItem } from "@/lib/content";

export const Route = createFileRoute("/services/$slug")({
  loader: async ({ params }) => {
    const categories = await loadServiceCategories();
    const category = categories.find((c) => c.slug === params.slug);
    if (!category) throw notFound();
    return { category };
  },
  head: ({ loaderData }) => {
    const cat = loaderData?.category;
    const title = cat ? `${cat.name} — KS Lau & Co` : "Services — KS Lau & Co";
    const desc = cat?.intro ?? "Professional services from KS Lau & Co.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
      ],
    };
  },
  component: ServiceDetailPage,
  notFoundComponent: () => (
    <div className="container-page py-40 text-center">
      <h1 className="text-2xl font-bold text-primary">Service not found</h1>
      <Link to="/services" className="mt-4 inline-block text-base font-semibold text-primary hover:underline">
        ← All services
      </Link>
    </div>
  ),
  errorComponent: ({ reset }) => (
    <div className="container-page py-40 text-center">
      <h1 className="text-2xl font-bold text-primary">Something went wrong</h1>
      <button onClick={reset} className="mt-4 text-base font-semibold text-primary hover:underline">Try again</button>
    </div>
  ),
});

function ServiceDetailPage() {
  const { category } = Route.useLoaderData() as { category: ServiceCategory };
  return (
    <>
      <SectionRail
        sections={[
          { id: "svc-overview", label: "Overview" },
          { id: "svc-details", label: "Details" },
          { id: "svc-contact", label: "Contact" },
        ]}
      />
      {/* Combined hero with image background */}
      <section className="relative isolate overflow-hidden ">
        <img
          src={category.image}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 -z-20 h-full w-full object-cover"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-t from-primary/90 via-primary/65 to-primary/20 md:bg-gradient-to-r md:from-primary/85 md:via-primary/55 md:to-transparent"
        />
        <Container>
          <div className="flex min-h-[32vh] flex-col justify-end py-10 text-primary-foreground md:min-h-[40vh] md:py-14">
            <Link
              to="/services"
              className="mb-6 inline-flex w-fit items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-white/80 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" /> All services
            </Link>
            <h1 className="mt-3 max-w-3xl text-3xl font-semibold leading-[1.15] tracking-tight md:text-4xl lg:text-5xl">
              {category.name}
            </h1>

          </div>
        </Container>
      </section>

      <section className="relative pb-20 pt-14 md:pb-28 md:pt-20">
        <Container>
          {/* Overview */}
          <div id="svc-overview" className="scroll-mt-24">
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-gold">Overview</p>
            <p className="mt-4 text-lg leading-relaxed text-foreground/85 md:text-xl md:leading-[1.7]">
              {category.intro}
            </p>
          </div>


          {/* Item tiles */}
          <div id="svc-details" className="mt-16 scroll-mt-24 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {category.items.map((it: ServiceItem, i) => (
              <Link key={it.name} to="/contact" className="group block h-full">
                <CardMotion
                  animateIndex={i}
                  className="group flex h-full flex-col overflow-hidden rounded-xl bg-white ring-1 ring-border/60 transition hover:-translate-y-0.5 hover:ring-gold hover:shadow-lg"
                >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img
                    src={it.image ?? category.image}
                    alt=""
                    aria-hidden="true"
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-gradient-to-t from-primary/70 via-primary/20 to-transparent"
                  />
                </div>
                <div className="flex flex-1 flex-col gap-2 p-5">
                  <h3 className="text-lg font-semibold leading-snug tracking-tight text-primary group-hover:text-gold">
                    {it.name}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{it.description}</p>
                </div>
                </CardMotion>
              </Link>
            ))}
          </div>

          <div id="svc-contact" className="scroll-mt-24">
          <GlassCard hover={false} className="mt-12 p-6 sm:p-10 md:p-12">

            <div className="grid items-center gap-6 md:grid-cols-[1.5fr_1fr]">
              <div>
                <h3 className="text-2xl font-bold tracking-tight text-primary md:text-3xl">
                  Speak to our team about {category.name}.
                </h3>
                <p className="mt-3 text-base leading-relaxed text-muted-foreground md:text-lg">
                  Tell us about your business and we'll arrange a confidential discussion with the right partner.
                </p>
              </div>
              <div className="md:justify-self-end">
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3.5 text-base font-semibold text-primary-foreground hover:bg-primary/90">
                  Make an enquiry <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </GlassCard>
          </div>

        </Container>
      </section>
    </>
  );
}
